/**
 * Звонок «обратным вызовом через АТС» от просьбы до конца разговора.
 *   1. POST telephony/calls → задание (job); ждём, принята ли команда АТС;
 *   2. после «принято» АТС звонит на телефон человека; пока он не ответил — шаг 0;
 *   3. следим за звонком сделки (GET calls?dealId): ringing → шаг 1 «соединяем», in_progress → шаг 2.
 * Время и ожидание подменяются в тестах (deps.sleep / deps.now).
 */
import { callPhaseOf, type CallPhase } from './callMode';
import type { CallBrief, TelephonyJob } from '@/api/crmCard';

export interface CallFlowDeps {
  requestCall: (b: { phone: string; dealId?: string; personId?: string }) => Promise<{ jobId: string }>;
  getJob: (id: string) => Promise<TelephonyJob>;
  getDealCalls: (dealId: string) => Promise<CallBrief[]>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
}

export interface CallFlowInput { phone: string; dealId?: string; personId?: string }

export type CallFlowEvent =
  | CallPhase
  /** Команда принята, но разговор отследить нельзя (нет сделки) или АТС не ответила вовремя. */
  | { kind: 'untracked'; note: string };

const JOB_WAIT_MS = 20_000;
const JOB_POLL_MS = 1_500;
const CALL_WAIT_MS = 3 * 60_000;
const CALL_POLL_MS = 2_000;

export async function runPbxCall(
  input: CallFlowInput,
  deps: CallFlowDeps,
  onEvent: (e: CallFlowEvent) => void,
  isCancelled: () => boolean,
): Promise<void> {
  const asked = deps.now();
  onEvent({ kind: 'working', step: 0 });
  let jobId: string;
  try {
    jobId = (await deps.requestCall(input)).jobId;
  } catch (e) {
    onEvent({ kind: 'failed', message: e instanceof Error && e.message ? e.message : 'Не удалось попросить АТС позвонить' });
    return;
  }

  // 1. принята ли команда
  let job: TelephonyJob | null = null;
  const until = deps.now() + JOB_WAIT_MS;
  while (!isCancelled()) {
    try { job = await deps.getJob(jobId); } catch { /* мигнула сеть — спросим ещё раз */ }
    if (job && (job.status === 'done' || job.status === 'failed')) break;
    if (deps.now() > until) break;
    await deps.sleep(JOB_POLL_MS);
  }
  if (isCancelled()) return;
  if (job?.status === 'failed') { onEvent(callPhaseOf(job, null, asked)); return; }
  if (!job || job.status !== 'done') {
    onEvent({ kind: 'untracked', note: 'АТС пока не ответила — если телефон не зазвонит, наберите номер вручную' });
    return;
  }
  if (!input.dealId) {
    onEvent({ kind: 'untracked', note: 'АТС звонит вам. Ответьте — она соединит с клиентом' });
    return;
  }

  // 2. следим за звонком
  const stop = deps.now() + CALL_WAIT_MS;
  let lastStep = -1;
  while (!isCancelled() && deps.now() < stop) {
    await deps.sleep(CALL_POLL_MS);
    let call: CallBrief | null = null;
    try { call = (await deps.getDealCalls(input.dealId))[0] ?? null; } catch { /* повторим */ }
    const phase = callPhaseOf(job, call, asked);
    if (phase.kind === 'ended') { onEvent(phase); return; }
    if (phase.kind === 'working' && phase.step !== lastStep) {
      lastStep = phase.step;
      onEvent(phase);
    }
  }
}
